/**
 * FP3-043 Phase C — "Show demo/test data" toggle.
 *
 * Server-side preference stored on the super admin's user row
 * (`super_admin_show_hidden_data`). Flipping this affects every
 * list endpoint that respects the hidden filter (Platform Overview,
 * /organizations, /admin/schools, /access-requests, /invites,
 * /admin/students, ...).
 *
 * Only renders for super_admin. For every other role the toggle is
 * a no-op surface and we hide it entirely.
 */
import { useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export default function ShowHiddenDataToggle({
  onChange,
  testid = "show-hidden-data-toggle",
  className = "",
}) {
  const { user, refresh } = useAuth();
  const isSuper = (user?.permission_role || "").toLowerCase() === "super_admin";
  const initial = !!user?.super_admin_show_hidden_data;
  const [showHidden, setShowHidden] = useState(initial);
  const [busy, setBusy] = useState(false);

  if (!isSuper) return null;

  const toggle = async () => {
    if (busy) return;
    const next = !showHidden;
    setBusy(true);
    try {
      await api.setShowHiddenData(next);
      setShowHidden(next);
      // Refresh the cached `user` so other surfaces hydrate with the
      // new value without a hard reload.
      if (typeof refresh === "function") {
        try {
          await refresh();
        } catch {
          /* non-fatal */
        }
      }
      if (typeof onChange === "function") onChange(next);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not update demo/test visibility"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      data-testid={testid}
      onClick={toggle}
      disabled={busy}
      variant="outline"
      className={
        (showHidden
          ? "border-[#9C6B2E] bg-[#FBF3E4] text-[#9C6B2E] hover:bg-[#F4E6CD]"
          : "border-[#E5E3DB] text-[#6B7280] hover:bg-[#F8F7F2]") +
        " " +
        className
      }
      title={
        showHidden
          ? "Currently showing demo / test / archived rows. Click to hide."
          : "Currently hiding demo / test / archived rows. Click to show."
      }
    >
      {showHidden ? (
        <Eye size={14} weight="duotone" className="mr-1.5" />
      ) : (
        <EyeSlash size={14} weight="duotone" className="mr-1.5" />
      )}
      {showHidden ? "Showing demo/test" : "Hide demo/test"}
    </Button>
  );
}
